import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Printer, CreditCard, Landmark, Banknote, ArrowLeft } from "lucide-react";
import { api } from "../lib/api";

const money = (v) => Number(v || 0).toLocaleString("en-US", { style: "currency", currency: "USD" });

const fmt = (d) => (d ? new Date(d.length === 10 ? `${d}T12:00:00` : d).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" }) : "—");
const BANK_LABELS = [["bank_name", "Bank"], ["account_name", "Beneficiary"], ["account_number", "Account #"], ["routing_number", "Routing (ABA)"], ["swift_code", "SWIFT / BIC"], ["bank_address", "Bank address"]];

const Option = ({ icon: I, title, children, testid, muted }) => (
  <div className={`border p-5 break-inside-avoid ${muted ? "border-slate-200 bg-slate-50" : "border-slate-300"}`} data-testid={testid}>
    <div className="flex items-center gap-2 font-display font-bold uppercase tracking-wide text-slate-900"><I className="w-4 h-4 text-orange-600" />{title}</div>
    <div className="mt-3 text-sm text-slate-700 space-y-1">{children}</div>
  </div>
);

const PaymentOptions = ({ inv }) => {
  const bank = inv.bank_details || {};
  const hasBank = BANK_LABELS.some(([k]) => bank[k]);
  return (
    <div className="grid md:grid-cols-3 gap-4" data-testid="invoice-payment-options">
      <Option icon={CreditCard} title="Credit card" testid="invoice-option-card">
        {inv.payment_link ? (
          <><p>Pay securely online with your card.</p><a href={inv.payment_link} target="_blank" rel="noreferrer" className="btn-amber h-10 mt-2 text-sm no-print" data-testid="invoice-pay-card-link">Pay by card</a><p className="hidden print:block break-all text-xs">{inv.payment_link}</p></>
        ) : <p>Your secure card payment link will be provided by our financial department.</p>}
      </Option>
      <Option icon={Landmark} title="Bank transfer / wire" testid="invoice-option-bank">
        {hasBank ? BANK_LABELS.filter(([k]) => bank[k]).map(([k, l]) => <p key={k}><span className="text-slate-500">{l}:</span> <span className="font-mono text-slate-900">{bank[k]}</span></p>)
          : <p>Wire details are provided by our financial department on request.</p>}
        {bank.reference_instructions && <p className="pt-1 text-xs text-slate-600">{bank.reference_instructions}</p>}
      </Option>
      <Option icon={Banknote} title="Cash on delivery" testid="invoice-option-cod" muted={!inv.cod_available}>
        <p>{inv.cod_available ? "Available when agreed with our team in advance. Payment is collected at delivery." : "Not available for this invoice."}</p>
      </Option>
    </div>
  );
};

export default function InvoicePage() {
  const { token } = useParams();
  const [d, setD] = useState(null);
  const [missing, setMissing] = useState(false);
  useEffect(() => { api.get(`/invoice/${token}`).then((r) => setD(r.data)).catch(() => setMissing(true)); }, [token]);

  if (missing) return <div className="min-h-screen grid place-items-center text-slate-600" data-testid="invoice-not-found">Invoice not found. <Link to="/" className="ml-2 text-brand-600 underline">Home</Link></div>;
  if (!d) return <div className="min-h-screen grid place-items-center text-slate-500">Loading invoice…</div>;
  const { invoice: inv, company: c, billed_to: bt, shipment: s } = d;
  return (
    <div className="min-h-screen bg-[#F7F8FA] print:bg-white py-8 print:py-0 px-4" data-testid="invoice-page">
      <div className="max-w-4xl mx-auto mb-4 flex justify-between items-center no-print">
        <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-brand-600"><ArrowLeft className="w-4 h-4" />{c.company}</Link>
        <button onClick={() => window.print()} className="btn-amber h-10 text-sm" data-testid="invoice-print-button"><Printer className="w-4 h-4" />Download / Print PDF</button>
      </div>
      <article className="relative max-w-4xl mx-auto bg-white border border-slate-200 print:border-0 shadow-sm print:shadow-none p-8 sm:p-12">
        {d.paid && <div className="absolute top-56 right-10 sm:right-14 rotate-[-12deg] border-4 border-emerald-600 text-emerald-700 font-display font-black text-4xl px-4 py-1 opacity-80 pointer-events-none" data-testid="invoice-paid-stamp">PAID</div>}
        <header className="flex flex-col sm:flex-row justify-between gap-6 pb-8 border-b-2 border-brand-600">
          <div>
            <div className="flex items-center gap-3">
              {c.logo_url ? <img src={c.logo_url} alt={`${c.company} logo`} className="w-16 h-16 rounded-full object-cover border border-slate-200" data-testid="invoice-logo" />
                : <span className="w-10 h-10 bg-amber-500 grid place-items-center font-display font-black text-slate-900 text-xl">ET</span>}
              <span className="font-display font-extrabold uppercase text-2xl text-brand-600">{c.company}</span>
            </div>
            <div className="mt-3 text-sm text-slate-600 leading-relaxed">{c.address_full}<br />{c.phone_display} · {c.email}<br />USDOT {c.usdot} · {c.mc}</div>
          </div>
          <div className="sm:text-right">
            <div className="font-display font-black uppercase text-4xl text-slate-900">Invoice</div>
            <div className="mt-1 font-mono text-lg text-slate-900" data-testid="invoice-number">{inv.number}</div>
            <div className="mt-2 text-sm text-slate-600">Issued {fmt(inv.created_at)}<br /><strong className="text-slate-900">Due {fmt(inv.due_date)}</strong></div>
          </div>
        </header>
        <section className="grid sm:grid-cols-2 gap-6 py-8">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Billed to</div>
            <div className="mt-2 text-slate-900 font-semibold" data-testid="invoice-billed-name">{bt.name}</div>
            {bt.company && <div className="text-sm text-slate-700">{bt.company}</div>}
            <div className="text-sm text-slate-700">{bt.email}</div>
          </div>
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Shipment</div>
            <div className="mt-2 font-mono text-slate-900" data-testid="invoice-tracking-number">{s.tracking_number}</div>
            <div className="text-sm text-slate-700">{s.item_summary}</div>
            <div className="text-sm text-slate-700">{s.origin.city}, {s.origin.state} → {s.destination.city}, {s.destination.state}</div>
            <div className="text-sm text-slate-700">Preferred pickup {fmt(s.pickup_date)}</div>
          </div>
        </section>
        <table className="w-full text-sm" data-testid="invoice-line-items-table">
          <thead><tr className="border-b border-slate-300 text-left font-mono text-[10px] uppercase tracking-[0.15em] text-slate-500"><th className="py-3">Description</th><th className="py-3 text-right">Amount</th></tr></thead>
          <tbody>
            {inv.line_items.map((li, i) => <tr key={i} className="border-b border-slate-100"><td className="py-3 text-slate-800">{li.label}</td><td className="py-3 text-right font-mono text-slate-900">{money(li.amount)}</td></tr>)}
          </tbody>
          <tfoot><tr><td className="pt-5 text-right font-display font-bold uppercase text-slate-900 text-lg">Total due ({inv.currency})</td><td className="pt-5 text-right font-display font-black text-3xl text-slate-900" data-testid="invoice-total-amount">{money(inv.total)}</td></tr></tfoot>
        </table>
        <section className="mt-10">
          <h2 className="font-display font-bold uppercase text-slate-900 text-xl mb-2">How to pay</h2>
          <p className="text-sm text-slate-700 mb-5 whitespace-pre-wrap" data-testid="invoice-instructions-text">{inv.payment_instructions}</p>
          <PaymentOptions inv={inv} />
        </section>
        {inv.notes && <section className="mt-8 text-sm text-slate-700 whitespace-pre-wrap border-l-2 border-amber-500 pl-4">{inv.notes}</section>}
        <footer className="mt-10 pt-6 border-t border-slate-200 text-xs text-slate-500">Transport is booked once payment clears. Questions? Call {c.phone_display} or email {c.email}. Please quote invoice {inv.number}.</footer>
      </article>
    </div>
  );
}
